import { useCallback } from "react";
import { ScenarioModel } from "../../clients/KompasApi";
import {
  Collapse,
  Table,
  Tbody,
  Td,
  Tr,
  useDisclosure,
} from "@chakra-ui/react";
import { useAtom } from "jotai";
import {
  highlightDifferencesAtom,
  onlyShowDifferencesAtom,
} from "../../pages/ComparePage";
import {
  extractValue,
  hasAnyMeasure,
  hasMeasure,
} from "../../shared/utils/measureUtils";
import { CategoryHeader } from "./CategoryHeader";
import { CategoryFooter } from "./CategoryFooter";
import { TaxKey } from "../TaxSelect";
import { useComparePageContext } from "../../shared/state/ComparePageContext";

export const tempCalculateCO2Impact = (scenario: ScenarioModel) =>
  scenario.calculation.yearlyCO2Emission /
  (scenario.centralHeatingSource?.id || 1);
export const tempCalculatePrice = (scenario: ScenarioModel) =>
  (scenario.centralHeatingSource?.id ?? 0) * 1000;

export function HeatingMeasuresSubTable() {
  const { scenarios, situation } = useComparePageContext();
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });

  const [onlyShowDifferences] = useAtom(onlyShowDifferencesAtom);
  const [highlightDifferences] = useAtom(highlightDifferencesAtom);

  const scenarioValuesDiffer = useCallback(
    (measure: keyof ScenarioModel) =>
      new Set(scenarios.map((scenario) => extractValue(scenario[measure])))
        .size > 1,
    [scenarios],
  );

  return (
    <Collapse
      in={hasAnyMeasure(
        [
          "centralHeatingSource",
          "centralHeatingSupplyTemperature",
          "tapWaterHeatingSource",
        ],
        scenarios,
        situation,
        onlyShowDifferences,
      )}
    >
      <Table
        className={isOpen ? "open" : "closed"}
        sx={{ borderSpacing: "2rem 0" }}
        variant="compare"
      >
        <CategoryHeader
          isOpen={isOpen}
          onToggle={onToggle}
        >
          Verwarming
        </CategoryHeader>
        <Tbody>
          {hasMeasure(
            "centralHeatingSource",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("centralHeatingSource")
                  ? "highlight"
                  : ""
              }
              data-id="measure-centralHeatingSource"
            >
              <Td>CV verwarmingsbron</Td>
              <Td textAlign="center">{situation.centralHeatingSource?.name}</Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.centralHeatingSource?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure(
            "centralHeatingSupplyTemperature",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("centralHeatingSupplyTemperature")
                  ? "highlight"
                  : ""
              }
              data-id="measure-centralHeatingSupplyTemperature"
            >
              <Td>CV aanvoertemperatuur</Td>
              <Td textAlign="center">
                {situation.centralHeatingSupplyTemperature?.name}
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.centralHeatingSupplyTemperature?.name}
                </Td>
              ))}
            </Tr>
          )}
          {hasMeasure(
            "tapWaterHeatingSource",
            scenarios,
            situation,
            onlyShowDifferences,
          ) && (
            <Tr
              className={
                highlightDifferences &&
                scenarioValuesDiffer("tapWaterHeatingSource")
                  ? "highlight"
                  : ""
              }
              data-id="measure-tapWaterHeatingSource"
            >
              <Td>Warmtapwater verwarmingsbron</Td>
              <Td textAlign="center">
                {situation.tapWaterHeatingSource?.name}
              </Td>
              {scenarios.map((scenario) => (
                <Td key={scenario.id} textAlign="center">
                  {scenario.tapWaterHeatingSource?.name}
                </Td>
              ))}
            </Tr>
          )}
        </Tbody>
        <CategoryFooter
          calculateCO2Impact={tempCalculateCO2Impact}
          calculatePrice={tempCalculatePrice}
          taxKey={TaxKey.Heating}
        />
      </Table>
    </Collapse>
  );
}
